import React from "react";
import { gettext } from "web/script/modules/django-i18n";
import { languageIs, languageIsEnglish } from "web/script/modules/language";
import { shouldShowOmnibusPages } from "web/script/modules/utils";
import { getUrl } from "web/script/routing/getUrl";
import { useFooterContext } from "./footer";
import FooterBlock from "./footer-block";
import styles from "./footer-help-info.module.css";
import { FooterUrls } from "./footer-urls";

export const HELP = "help";

function FooterHelpInfo(): JSX.Element {
    const {
        helpCenterUrls,
        openBlockId,
        setOpenBlock,
        showCookieSettingsOption,
        openCookieSettingsModal,
        country,
    } = useFooterContext();

    return (
        <FooterBlock
            title={gettext("footer.help_info.title_variant_b")}
            isOpen={openBlockId == HELP}
            onClick={(): void => setOpenBlock(HELP)}
        >
            <ul>
                <li>
                    <a className={styles.link} href={helpCenterUrls["homepage"]}>
                        {gettext("footer.help_info.help_center_link")}
                        {/* Help Center */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={getUrl("about")}>
                        {gettext("footer.links_about_us")}
                        {/* About us */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={helpCenterUrls["shipping_policy"]}>
                        {gettext("footer.help_info.shipping_policy_link")}
                        {/* Shipping Policy */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={helpCenterUrls["returns_policy"]}>
                        {gettext("footer.help_info.returns_policy_link")}
                        {/* Returns Policy */}
                    </a>
                </li>
                {languageIsEnglish() && (
                    <>
                        <li className="footer-link-item">
                            <a className={styles.link} href={helpCenterUrls["payments"]}>
                                {gettext("footer.help_info.payments_link")}
                            </a>
                            {/* Payments */}
                        </li>
                        <li className="footer-link-item">
                            <a className={styles.link} href={helpCenterUrls["refund_policy"]}>
                                {gettext("footer.help_info.refund_policy_link")}
                            </a>
                            {/* Refunds Policy */}
                        </li>
                        <li>
                            <a className={styles.link} href={FooterUrls["makingLyst"]}>
                                {gettext("footer.help_info.developers_link")}
                                {/* Developers */}
                            </a>
                        </li>
                    </>
                )}
                <li>
                    <a className={styles.link} href={getUrl("careers")}>
                        {gettext("footer.links_careers")}
                        {/* Careers */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={getUrl("helpContactUs")}>
                        {gettext("footer.help_info.contact_link")}
                        {/* Contact */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={getUrl("helpTermsAndConditions")}>
                        {gettext("footer.help_info.terms_and_conditions_link")}
                        {/* Terms & Conditions */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={getUrl("helpPrivacyPolicy")}>
                        {gettext("footer.help_info.privacy_and_cookie_policy_link")}
                        {/* Privacy & Cookie Policy */}
                    </a>
                </li>
                <li>
                    <a className={styles.link} href={getUrl("helpIntellectualProperty")}>
                        {gettext("footer.help_info.intellectual_property_link")}
                        {/* Intellectual Property */}
                    </a>
                </li>
                {shouldShowOmnibusPages() && (
                    <li>
                        <a className={styles.link} href={getUrl("helpProductRanking")}>
                            {gettext("footer.help_info.product_ranking_link")}
                            {/* Product ranking information */}
                        </a>
                    </li>
                )}
                {shouldShowOmnibusPages() && (
                    <li>
                        <a className={styles.link} href={getUrl("helpPrivateSellers")}>
                            {gettext("footer.help_info.private_sellers_link")}
                            {/* Buying from individuals */}
                        </a>
                    </li>
                )}
                <li>
                    <a className={styles.link} href={getUrl("sitemapsAllCategories")}>
                        {gettext("footer.links_categories")}
                        {/* Categories */}
                    </a>
                </li>
                {/* German language footer gets an extra link to Impressum section of the privacy policy. */}
                {languageIs("DE") && (
                    <li>
                        <a
                            className={styles.link}
                            href={`${getUrl("helpPrivacyPolicy")}#impressum`}
                        >
                            {"Impressum"}
                        </a>
                    </li>
                )}
                <li>
                    <a className={styles.link} href={getUrl("partnerContact")}>
                        {gettext("footer.help_info.become_a_partner_link")}
                        {/* Become a Partner */}
                    </a>
                </li>
                {languageIsEnglish() && (
                    <>
                        <li>
                            <a className={styles.link} href={FooterUrls["lystInsights"]}>
                                {gettext("footer.links_lyst_insights")}
                                {/* Lyst Insights */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={FooterUrls["lystNews"]}>
                                {gettext("footer.links_lyst_news")}
                                {/* Lyst News */}
                            </a>
                        </li>
                    </>
                )}
                {showCookieSettingsOption && (
                    <li>
                        <a className={styles.link} onClick={openCookieSettingsModal}>
                            {gettext("cookie_notification.cookie_settings.title")}
                            {/* Cookie Settings */}
                        </a>
                    </li>
                )}
                {country === "US" && (
                    <>
                        <li>
                            <a className={styles.link} href={FooterUrls["lystEditorial"]}>
                                {gettext("footer.links_lyst_editorial")}
                                {/* Lyst Editorial */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={`${getUrl("helpPrivacyPolicy")}#ccpa`}>
                                {gettext("footer.links_california_privacy_rights")}
                                {/* Rights under CCPA */}
                            </a>
                        </li>
                    </>
                )}
            </ul>
        </FooterBlock>
    );
}

export default FooterHelpInfo;
